import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from './api';
import { getRuntimeEnv, isDevMode } from './runtimeEnv';
import { DEFAULT_BRANDING } from '@/theme';

const MODE_STORAGE_KEY = 'app_theme_mode';
const FONT_LINK_ID = 'branding-google-fonts';

const BrandingContext = createContext(null);

export const useBranding = () => {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within BrandingProvider');
  return ctx;
};

export const resolveTenantSlug = () => {
  const envSlug = getRuntimeEnv('REACT_APP_TENANT_SLUG', 'gymify');
  if (typeof window === 'undefined') return envSlug || 'gymify';
  const stored = window.localStorage.getItem('tenant_slug');
  if (stored && !isDevMode()) {
    // Outside dev mode the env-configured tenant is authoritative; the
    // localStorage override only exists for the demo picker and superadmin
    // impersonation (which needs branding to follow the impersonated tenant).
    let allowOverride = false;
    try {
      const u = JSON.parse(window.localStorage.getItem('gymify_user') || 'null');
      allowOverride = Boolean(u && (u.role === 'superadmin' || u.act_as_tenant_id));
    } catch {
      allowOverride = false;
    }
    if (!allowOverride) return envSlug || 'gymify';
  }
  return stored || envSlug || 'gymify';
};

const applyFavicon = (url) => {
  if (!url || typeof document === 'undefined') return;
  let link = document.querySelector("link[rel='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
};

const fontFamilyParam = (name) => {
  const trimmed = (name || '').trim();
  if (!trimmed) return null;
  return `family=${encodeURIComponent(trimmed).replace(/%20/g, '+')}:wght@400;500;600;700;800;900`;
};

// Also used by standalone public pages (e.g. the lead form) that render outside
// BrandingProvider; they pass their own linkId so they don't clobber the app's link.
export const applyGoogleFonts = (branding, linkId = FONT_LINK_ID) => {
  if (typeof document === 'undefined') return;
  const families = new Set();
  if (branding.font_heading) families.add(branding.font_heading);
  if (branding.font_body) families.add(branding.font_body);
  if (families.size === 0) return;

  const params = Array.from(families).map(fontFamilyParam).filter(Boolean);
  if (params.length === 0) return;
  const href = `https://fonts.googleapis.com/css2?${params.join('&')}&display=swap`;

  let link = document.getElementById(linkId);
  if (!link) {
    link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loaded, setLoaded] = useState(false);
  const [mode, setModeState] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return window.localStorage.getItem(MODE_STORAGE_KEY) || 'dark';
  });
  const [userOverrodeMode, setUserOverrodeMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(MODE_STORAGE_KEY) !== null;
  });

  useEffect(() => {
    const tenantSlug = resolveTenantSlug();
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/branding/public', { params: { tenant_slug: tenantSlug } });
        if (cancelled) return;
        setBranding({ ...DEFAULT_BRANDING, ...data });
        if (!userOverrodeMode && (data.default_mode === 'light' || data.default_mode === 'dark')) {
          setModeState(data.default_mode);
        }
      } catch (err) {
        // Fall back to baked-in defaults — login still works without branding API.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userOverrodeMode]);

  useEffect(() => {
    if (typeof document !== 'undefined' && branding.app_name) {
      document.title = branding.tagline
        ? `${branding.app_name} — ${branding.tagline}`
        : branding.app_name;
    }
    applyFavicon(branding.favicon_url);
    applyGoogleFonts(branding);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    branding.app_name,
    branding.tagline,
    branding.favicon_url,
    branding.font_heading,
    branding.font_body,
  ]);

  const setMode = useCallback((next) => {
    setModeState(next);
    setUserOverrodeMode(true);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(MODE_STORAGE_KEY, next);
    }
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setMode]);

  const refresh = useCallback(async () => {
    const tenantSlug = resolveTenantSlug();
    const { data } = await api.get('/branding/public', { params: { tenant_slug: tenantSlug } });
    setBranding({ ...DEFAULT_BRANDING, ...data });
    return data;
  }, []);

  const value = useMemo(
    () => ({ branding, mode, setMode, toggleMode, loaded, refresh }),
    [branding, mode, setMode, toggleMode, loaded, refresh],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};
